import {
    FailureReason,
    type IndicatorInput,
    useValidateCredentialInputQuery,
} from "@/src/graphql/generated/api/graphql";
import { useMemo } from "react";
import { usePaginatedSubjectDids } from "../usePaginatedSubjectDids/usePaginatedSubjectDids";

export type useGetFailuresForSubjectDidsArgs = {
    formSubjectDids: (string | number)[];
    selectedIndicators: IndicatorInput[];
    version: string;
    exchangeProfile: {
        source: string;
    };
};

export const useGetFailuresForSubjectDids = ({
    formSubjectDids,
    selectedIndicators,
    version,
    exchangeProfile,
}: useGetFailuresForSubjectDidsArgs) => {
    const noSubjectDids =
        !Array.isArray(formSubjectDids) || formSubjectDids.length === 0;

    const noIndicators =
        !Array.isArray(selectedIndicators) || selectedIndicators.length === 0;

    const { data, loading: validationLoading } =
        useValidateCredentialInputQuery({
            skip:
                noSubjectDids ||
                noIndicators ||
                !version ||
                !exchangeProfile?.source,
            variables: {
                input: {
                    branchOrTag: version,
                    repositoryUrl: exchangeProfile?.source || "",
                },
                indicators: selectedIndicators,
                subjectIds: formSubjectDids,
            },
            onError: (error) => {
                console.error("Error validating credential input:", error);
            },
        });

    const failures =
        data?.validateCredentialsCanBeIssuedForInput?.failures || [];

    const { subjectDids } = usePaginatedSubjectDids(
        0,
        999,
        !open || failures.length === 0
    );

    const mappedFailuresWithSubjectDids = useMemo(() => {
        return failures?.map((failure) => {
            const subjectDid = subjectDids.find(
                (did) => did.id === failure.subjectId
            );
            return {
                ...failure,
                subjectDidName: subjectDid?.name || "",
            };
        });
    }, [failures, subjectDids]);

    const uniqueSubjectDidCount = useMemo(() => {
        const uniqueIds = new Set(
            mappedFailuresWithSubjectDids?.map((failure) => failure.subjectId)
        );
        return uniqueIds.size;
    }, [mappedFailuresWithSubjectDids]);

    const conflictingCredentials = useMemo(() => {
        return mappedFailuresWithSubjectDids?.filter(
            (failure) => failure.reason === FailureReason.ConflictingCredential
        );
    }, [mappedFailuresWithSubjectDids]);

    const missingHealthcareOfficeIris = useMemo(() => {
        return mappedFailuresWithSubjectDids?.filter(
            (failure) =>
                failure.reason === FailureReason.MissingHealthcareOfficeIri
        );
    }, [mappedFailuresWithSubjectDids]);

    return {
        failures: mappedFailuresWithSubjectDids,
        loading: validationLoading,
        uniqueSubjectDidCount,
        conflictingCredentials,
        missingHealthcareOfficeIris,
        hasConflictingCredentials: conflictingCredentials?.length > 0,
        hasMissingHealthcareOfficeIris: missingHealthcareOfficeIris?.length > 0,
        hasFailures: mappedFailuresWithSubjectDids?.length > 0,
    };
};

export default useGetFailuresForSubjectDids;
